import React from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors, SIZES } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';

/**
 * VERIFICACIÓN DE IDENTIDAD (antes "Biometría y Seguridad")
 * ----------------------------------------------------------------------------
 * ⚠️ REGLA AL EDITAR ESTA PANTALLA
 *
 * Esta pantalla es la que MÁS FÁCIL contradice la Política de Privacidad, y el
 * revisor de Google Play lee las dos. La política (punto 2.c) y los Términos
 * (punto 4) declaran, correctamente, que Go Safely NO usa reconocimiento
 * facial: no hay Rekognition, no hay prueba de vida, no se generan plantillas
 * ni vectores faciales. Lo único que ocurre es que se guardan dos fotos —la
 * cédula y una selfie— y las compara A MANO una persona del equipo
 * (`reviewKycSubmission`, functions/src/kyc.ts).
 *
 * Antes esta pantalla se llamaba "Biometría y Seguridad", su tarjeta decía
 * "Autenticación Facial" y afirmaba que la biometría era obligatoria para
 * aceptar un viaje. Eso es exactamente lo que Google marca como declaración
 * de datos biométricos que no cuadra con la política: rechazo, no observación.
 *
 * Lo que SÍ existe y aquí se describe sin adornos:
 *   1. KYC humano: foto del frente de la cédula + selfie → las compara una
 *      persona. La selfie pasa a ser la foto de perfil (la ve el pasajero).
 *   2. El candado del TELÉFONO al aceptar un viaje o un pedido: es Face ID,
 *      huella o PIN del propio sistema operativo (expo-local-authentication,
 *      services/biometricService.ts). Go Safely nunca recibe esa huella ni ese
 *      rostro — solo un sí/no del sistema. Y si el teléfono no tiene bloqueo
 *      configurado, `authenticateUser` deja pasar a propósito (un emulador
 *      limpio, el de Google, no puede quedar encerrado). Por eso NO se puede
 *      decir "obligatoria" ni "no se puede desactivar": lo controla el usuario
 *      desde los ajustes de su teléfono.
 *
 * El botón lleva a /kyc-welcome, que es el inicio real del embudo. Antes iba a
 * /kyc-biometrics, que es el ÚLTIMO paso y aborta con "Falta la cédula" si se
 * entra directo: un botón que solo sabe dar error.
 */
export default function IdentityVerificationScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  // `isVerified` es el sello que escribe el servidor cuando un humano aprueba
  // la solicitud. No hay un tercer estado en el store, así que no se puede
  // afirmar "en revisión": el texto de abajo cubre los dos casos posibles.
  const verificado = useAppStore((s) => s.isVerified);

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { borderBottomColor: colors.border, backgroundColor: colors.card }]}>
        <TouchableOpacity
          onPress={() => (router.canGoBack() ? router.back() : router.replace('/'))}
          style={styles.backButton}
          accessibilityRole="button"
          accessibilityLabel="Volver"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Verificación de Identidad</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.illustration}>
          <Ionicons name="id-card-outline" size={84} color={colors.primary} />
        </View>

        <Text style={[styles.description, { color: colors.textSecondary }]}>
          Para usar Go Safely verificamos quién eres con dos fotos: el{' '}
          <Text style={[styles.strong, { color: colors.text }]}>frente de tu cédula</Text> y una{' '}
          <Text style={[styles.strong, { color: colors.text }]}>selfie</Text>. Las compara a mano una
          persona de nuestro equipo.
        </Text>

        {/* ESTADO REAL. Aquí hubo un <Switch disabled value={true}> que no se
            movía al tocarlo y además decía "activado" aunque el usuario nunca
            hubiera hecho la validación. Un control muerto que muestra un dato
            falso es el rechazo vc13 de Google ("botones que no responden"). */}
        <View style={[styles.settingCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.settingInfo}>
            <Ionicons
              name={verificado ? 'shield-checkmark' : 'shield-outline'}
              size={24}
              color={verificado ? colors.success : colors.textSecondary}
              style={{ marginRight: 15 }}
            />
            <View style={{ flex: 1 }}>
              <Text style={[styles.settingTitle, { color: colors.text }]}>Estado de tu identidad</Text>
              <Text style={[styles.settingState, { color: colors.textSecondary }]}>
                {verificado ? 'Verificada por nuestro equipo' : 'Pendiente de verificar'}
              </Text>
            </View>
          </View>
          <Ionicons
            name={verificado ? 'checkmark-circle' : 'alert-circle'}
            size={26}
            color={verificado ? colors.success : '#FF9500'}
          />
        </View>

        {!verificado && (
          <>
            <TouchableOpacity
              style={[styles.ctaBtn, { backgroundColor: colors.primary }]}
              // A /kyc-welcome, que es donde empieza el embudo completo (elegir
              // perfil → cédula → selfie). Es el mismo destino que usan Perfil,
              // el mapa y el guard de _layout.tsx.
              onPress={() => router.push('/kyc-welcome')}
              accessibilityRole="button"
            >
              <Ionicons name="camera" size={20} color="#FFF" style={{ marginRight: 8 }} />
              <Text style={styles.ctaText}>Verificar mi identidad</Text>
            </TouchableOpacity>
            <Text style={[styles.note, { color: colors.textSecondary }]}>
              Si ya enviaste tus fotos, no tienes que hacer nada: una persona de nuestro equipo las
              está revisando y te avisamos por notificación cuando termine.
            </Text>
          </>
        )}

        <Text style={[styles.h3, { color: colors.text }]}>Qué hacemos y qué no</Text>
        <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.bulletRow}>
            <Ionicons name="person-outline" size={18} color={colors.primary} style={styles.bulletIcon} />
            <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
              <Text style={[styles.strong, { color: colors.text }]}>Te revisa una persona.</Text> No usamos
              reconocimiento facial, prueba de vida ni ningún sistema automático, y no creamos plantillas
              ni vectores de tu rostro.
            </Text>
          </View>
          <View style={styles.bulletRow}>
            <Ionicons name="lock-closed-outline" size={18} color={colors.primary} style={styles.bulletIcon} />
            <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
              <Text style={[styles.strong, { color: colors.text }]}>Tu cédula es privada.</Text> Queda en una
              carpeta a la que solo accedes tú y el equipo de revisión. Tu selfie, en cambio, pasa a ser tu
              foto de perfil y la ven los usuarios con los que te conectas.
            </Text>
          </View>
          <View style={styles.bulletRow}>
            <Ionicons name="archive-outline" size={18} color={colors.primary} style={styles.bulletIcon} />
            <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
              <Text style={[styles.strong, { color: colors.text }]}>Las dos fotos se conservan</Text> mientras
              tu cuenta exista: sirven para resolver una suplantación o una denuncia. Al eliminar tu cuenta
              se borran.
            </Text>
          </View>
        </View>

        <Text style={[styles.h3, { color: colors.text }]}>El bloqueo de tu teléfono</Text>
        <View style={[styles.infoCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.bulletRow}>
            <Ionicons name="phone-portrait-outline" size={18} color={colors.primary} style={styles.bulletIcon} />
            <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
              Al aceptar un viaje o un pedido, la app le pide a tu teléfono que confirme que eres tú con{' '}
              <Text style={[styles.strong, { color: colors.text }]}>Face ID, huella o PIN</Text>. Ese candado
              es del sistema operativo: Go Safely recibe solo un sí o un no, nunca tu huella ni tu rostro.
            </Text>
          </View>
          <View style={styles.bulletRow}>
            <Ionicons name="settings-outline" size={18} color={colors.primary} style={styles.bulletIcon} />
            <Text style={[styles.bulletText, { color: colors.textSecondary }]}>
              Lo activas o lo quitas desde los ajustes de tu teléfono, no desde aquí. Si tu teléfono no tiene
              bloqueo configurado, ese paso se omite y puedes seguir usando la app.
            </Text>
          </View>
        </View>

        <TouchableOpacity onPress={() => router.push('/privacy')} accessibilityRole="button">
          <Text style={[styles.link, { color: colors.primary }]}>Leer la Política de Privacidad</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
  },
  backButton: { padding: 8, marginLeft: -8 },
  headerTitle: { fontSize: 18, fontWeight: 'bold' },
  container: { padding: SIZES.padding, paddingBottom: 40, alignItems: 'center' },
  illustration: { marginTop: 20, marginBottom: 24, opacity: 0.9 },
  description: { textAlign: 'center', fontSize: 15, lineHeight: 22, marginBottom: 24 },
  strong: { fontWeight: '700' },
  settingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    width: '100%',
  },
  settingInfo: { flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: 10 },
  settingTitle: { fontSize: 16, fontWeight: 'bold' },
  settingState: { fontSize: 12, marginTop: 2 },
  ctaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
    borderRadius: 14,
    width: '100%',
    marginTop: 16,
  },
  ctaText: { color: '#FFF', fontSize: 15, fontWeight: 'bold' },
  note: { marginTop: 12, fontSize: 12, textAlign: 'center', lineHeight: 18 },
  h3: { fontSize: 15, fontWeight: '800', alignSelf: 'flex-start', marginTop: 28, marginBottom: 10 },
  infoCard: { width: '100%', borderRadius: 16, borderWidth: 1, padding: 16 },
  bulletRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14 },
  bulletIcon: { marginRight: 10, marginTop: 2 },
  bulletText: { flex: 1, fontSize: 13, lineHeight: 20 },
  link: { marginTop: 26, fontSize: 14, fontWeight: '600', textDecorationLine: 'underline' },
});
